/**
 * A ÚNICA renovação da sessão — vale para todas as abas do navegador e para as chamadas simultâneas da mesma aba.
 *
 * O `/auth/refresh` devolve um refresh NOVO e revoga o velho (aceito de volta só por 120 s, para a retentativa de quem não recebeu a
 * resposta). O front antigo guardava apenas o access e reaproveitava o refresh velho: na 2ª renovação o servidor via um refresh já
 * girado, acusava reuso e derrubava a pessoa em todos os aparelhos. Duas abas renovando juntas dariam o mesmo resultado: a segunda
 * mandaria o refresh que a primeira acabou de girar.
 *
 * Por isso a renovação é uma só:
 * - na mesma aba, chamadas simultâneas dividem a mesma promessa (o 401 de várias requisições ao mesmo tempo vira UM pedido);
 * - entre abas, a trava `navigator.locks` serializa. O refresh é lido ANTES de pedir a trava e relido DENTRO dela: se mudou, outra
 *   aba renovou enquanto esta esperava, e o par novo já está no armazenamento — não se pede nada à rede;
 * - sem `navigator.locks` (Safari antigo) não há trava entre abas, só a promessa da aba.
 *
 * Aqui NADA se apaga do armazenamento, em nenhum resultado: quem chama decide o logout. `"recusou"` é o servidor negando (401/403) ou
 * a sessão já não existir; `"tentar_depois"` é tudo o que não prova o fim da sessão (manutenção 503, queda de rede, resposta
 * estranha, pedido que passou de `PRAZO_DA_RENOVACAO_MS`) — esse NÃO derruba ninguém.
 *
 * Módulo puro de propósito — sem contexto, sem axios, sem `@/services/api`: serve ao `AuthContext` e aos interceptadores do axios, e por
 * isso usa `fetch` — a renovação não pode passar pelos próprios interceptadores.
 */
import { ehIos, estaInstalado } from "@/lib/pwaInstall";

/** Onde a sessão mora no navegador (o mesmo `seweb_auth` do `AuthContext`). */
export const CHAVE_DA_SESSAO = "seweb_auth";

/** Nome da trava entre abas. */
const NOME_DA_TRAVA = "seweb:renovar-sessao";

/** `VITE_API_URL` JÁ traz o `/api/v1`: o caminho nunca leva o prefixo de novo. Mesma base do `AuthContext`. */
const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000/api/v1";

/**
 * Prazo do pedido à rede. Um `fetch` pendurado (rede ruim de celular) seguraria a trava `seweb:renovar-sessao` de TODAS as abas, e quem
 * espera a renovação (o 401 das requisições, o timer do `AuthContext`) ficaria esperando sem fim. Abortar é seguro: se o servidor chegou
 * a girar o refresh e a resposta se perdeu, a próxima tentativa, com o refresh velho, cai na janela de 120 s do backend e renova sem
 * derrubar ninguém.
 */
export const PRAZO_DA_RENOVACAO_MS = 15_000;

export type ResultadoDaRenovacao = "renovou" | "recusou" | "tentar_depois";

type Sessao = Record<string, unknown>;

/**
 * Qual aparelho pede a renovação: o servidor dá prazos de sessão diferentes ao app instalado (45 dias sem abrir) e ao navegador (2 dias
 * do último uso).
 *
 * "Instalado" é o app da tela de início do CELULAR: `display-mode: standalone` E aparelho móvel (`ehIos()` já cobre o iPad que se passa
 * por Mac; Android pelo userAgent). O Chrome e o Edge do computador também abrem o PWA em `standalone` (um clique em "Instalar"), mas
 * computador é compartilhado — loja, balcão — e a decisão do Augusto (29/10/2026) é de 2 dias sem uso para ele, instalado ou não. Por
 * isso `standalone` sozinho NÃO basta.
 */
export function aparelhoAtual(): "instalado" | "navegador" {
  if (!estaInstalado()) return "navegador";
  const celular = ehIos() || /Android/i.test(navigator.userAgent);
  return celular ? "instalado" : "navegador";
}

/** A sessão guardada, ou `null` (sem sessão, JSON ilegível ou armazenamento bloqueado — o mesmo que o `AuthContext` trata como sem sessão). */
function lerSessao(): Sessao | null {
  try {
    const bruto = window.localStorage.getItem(CHAVE_DA_SESSAO);
    if (!bruto) return null;
    const sessao: unknown = JSON.parse(bruto);
    return sessao !== null && typeof sessao === "object" ? (sessao as Sessao) : null;
  } catch {
    return null;
  }
}

/** O refresh da sessão, ou `null` quando não há sessão ou ela não tem refresh. */
function refreshDe(sessao: Sessao | null): string | null {
  const refresh = sessao?.refresh_token;
  return typeof refresh === "string" && refresh !== "" ? refresh : null;
}

/** Um 200 do `/auth/refresh` só vale se trouxer o par inteiro e o prazo: nunca se grava a sessão pela metade. */
function parValido(
  corpo: unknown
): corpo is { access_token: string; refresh_token: string; expires_in: number } {
  if (corpo === null || typeof corpo !== "object") return false;
  const { access_token, refresh_token, expires_in } = corpo as Record<string, unknown>;
  return (
    typeof access_token === "string" &&
    access_token !== "" &&
    typeof refresh_token === "string" &&
    refresh_token !== "" &&
    typeof expires_in === "number" &&
    Number.isFinite(expires_in)
  );
}

type ParNovo = { access_token: string; refresh_token: string; expires_in: number };

/**
 * O pedido à rede, com prazo (`PRAZO_DA_RENOVACAO_MS`, que cobre também a leitura do corpo). Devolve o par novo, ou o resultado final
 * quando não há par para gravar. Estourar o prazo, cair a rede ou vir um corpo que não é JSON LANÇA: quem chama trata como `tentar_depois`.
 */
async function pedirOParNovo(refresh: string): Promise<ParNovo | ResultadoDaRenovacao> {
  const controle = new AbortController();
  const relogio = setTimeout(() => controle.abort(), PRAZO_DA_RENOVACAO_MS);
  try {
    const resposta = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refresh, aparelho: aparelhoAtual() }),
      signal: controle.signal,
    });

    if (resposta.status === 401 || resposta.status === 403) return "recusou";
    if (resposta.status !== 200) return "tentar_depois"; // 503 de manutenção, 5xx, proxy: não prova o fim da sessão

    const corpo: unknown = await resposta.json();
    return parValido(corpo) ? corpo : "tentar_depois";
  } finally {
    clearTimeout(relogio); // o pedido terminou (bem, mal ou abortado): o prazo não pode disparar depois, num pedido que já acabou
  }
}

/** O que roda DENTRO da trava (ou direto, sem ela). `refreshVisto` é o refresh que esta aba tinha ao começar a esperar. */
async function renovarAgora(refreshVisto: string): Promise<ResultadoDaRenovacao> {
  try {
    const sessao = lerSessao();
    const refresh = refreshDe(sessao);
    if (refresh === null) return "recusou"; // a sessão acabou (ex.: "Sair" noutra aba) enquanto esta esperava a trava
    // Outra aba girou o refresh enquanto esta esperava: o par novo já está guardado, pedir de novo mandaria o refresh velho.
    if (refresh !== refreshVisto) return "renovou";

    const par = await pedirOParNovo(refresh);
    if (typeof par === "string") return par;

    // O pedido levou tempo: a sessão pode ter mudado. Grava por cima da MAIS RECENTE (o `user` e os outros campos não se perdem) e
    // só se ela ainda for a mesma que pediu — se a pessoa saiu noutra aba, a sessão não ressuscita; se outro login a trocou, o par
    // que acabou de chegar é do usuário anterior e não vale por cima do novo.
    const atual = lerSessao();
    const refreshAtual = refreshDe(atual);
    if (atual === null || refreshAtual === null) return "recusou";
    if (refreshAtual !== refresh) return "renovou";

    window.localStorage.setItem(
      CHAVE_DA_SESSAO,
      JSON.stringify({
        ...atual,
        access_token: par.access_token,
        refresh_token: par.refresh_token,
        expires_at: Date.now() + par.expires_in * 1000,
      })
    );
    return "renovou";
  } catch {
    // prazo estourado, fetch sem rede, corpo que não é JSON, armazenamento cheio: nenhum prova o fim da sessão. Se o servidor já girou o
    // refresh e a resposta se perdeu (ou a gravação falhou), o velho ainda vale por 120 s para a próxima tentativa.
    return "tentar_depois";
  }
}

async function renovarComTrava(): Promise<ResultadoDaRenovacao> {
  // Visto ANTES de pedir a trava: é a referência para saber se outra aba renovou enquanto esta esperava.
  const refreshVisto = refreshDe(lerSessao());
  if (refreshVisto === null) return "recusou";

  const travas = typeof navigator === "undefined" ? undefined : navigator.locks;
  if (!travas?.request) return renovarAgora(refreshVisto);

  try {
    return await travas.request(NOME_DA_TRAVA, () => renovarAgora(refreshVisto));
  } catch {
    // A trava em si falhou (SecurityError em contexto sem permissão): é melhor renovar sem ela que deixar a pessoa sem renovação.
    // `renovarAgora` nunca lança, então este catch só pega a falha de `request` e o pedido não é feito duas vezes.
    return renovarAgora(refreshVisto);
  }
}

let renovacaoEmAndamento: Promise<ResultadoDaRenovacao> | null = null;

/**
 * Renova o par access/refresh. Nunca rejeita: o resultado é um dos três de `ResultadoDaRenovacao`. A promessa compartilhada da aba é
 * limpa quando termina (sucesso ou falha), para a próxima renovação pedir de novo.
 */
export function renovarSessao(): Promise<ResultadoDaRenovacao> {
  if (renovacaoEmAndamento) return renovacaoEmAndamento;
  const renovacao = renovarComTrava().finally(() => {
    if (renovacaoEmAndamento === renovacao) renovacaoEmAndamento = null;
  });
  renovacaoEmAndamento = renovacao;
  return renovacao;
}
